/**
 * Network Status Monitoring Service
 *
 * Detects online/offline state using navigator.onLine and periodic ping.
 * Provides event-based notifications for network status changes and
 * supports queuing actions while offline.
 */

import { errorLoggingService } from './errorLoggingService';

export type NetworkStatus = 'online' | 'offline';

export interface QueuedAction {
  id: string;
  action: () => Promise<unknown>;
  description: string;
  timestamp: number;
}

export interface NetworkStatusConfig {
  /** Interval in ms for periodic connectivity checks (default: 30000) */
  pingInterval: number;
  /** URL to ping for connectivity check (default: backend health endpoint) */
  pingUrl: string;
  /** Timeout in ms for ping requests (default: 5000) */
  pingTimeout: number;
  /** Whether to enable periodic pinging (default: true) */
  enablePing: boolean;
}

type StatusChangeListener = (status: NetworkStatus) => void;

const DEFAULT_CONFIG: NetworkStatusConfig = {
  pingInterval: 30000,
  pingUrl: '/api/health',
  pingTimeout: 5000,
  enablePing: true,
};

export class NetworkStatusService {
  private status: NetworkStatus = 'online';
  private listeners: StatusChangeListener[] = [];
  private queuedActions: QueuedAction[] = [];
  private pingIntervalId: ReturnType<typeof setInterval> | null = null;
  private config: NetworkStatusConfig;
  private isInitialized = false;

  constructor(config?: Partial<NetworkStatusConfig>) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.status =
      typeof navigator !== 'undefined' ? (navigator.onLine ? 'online' : 'offline') : 'online';
  }

  /**
   * Initialize the network status monitoring
   */
  initialize(config?: Partial<NetworkStatusConfig>): void {
    if (this.isInitialized) return;

    if (config) {
      this.config = { ...this.config, ...config };
    }

    this.isInitialized = true;

    // Listen for browser online/offline events
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);
    }

    // Start periodic ping if enabled
    if (this.config.enablePing) {
      this.startPing();
    }
  }

  /**
   * Handle browser online event
   */
  private handleOnline = (): void => {
    this.updateStatus('online');
  };

  /**
   * Handle browser offline event
   */
  private handleOffline = (): void => {
    this.updateStatus('offline');
  };

  /**
   * Update the network status and notify listeners
   */
  private updateStatus(newStatus: NetworkStatus): void {
    if (this.status === newStatus) return;

    const previousStatus = this.status;
    this.status = newStatus;

    // Log status change
    if (newStatus === 'offline') {
      errorLoggingService.logWarning({
        message: 'Network connection lost',
        action: 'network-status-change',
        metadata: { previousStatus, newStatus },
      });
    } else {
      errorLoggingService.logInfo({
        message: 'Network connection restored',
        action: 'network-status-change',
        metadata: { previousStatus, newStatus },
      });

      // Process queued actions when coming back online
      this.processQueue();
    }

    // Notify listeners
    this.listeners.forEach((listener) => listener(newStatus));
  }

  /**
   * Start periodic connectivity ping
   */
  private startPing(): void {
    if (this.pingIntervalId) return;

    this.pingIntervalId = setInterval(() => {
      this.checkConnectivity();
    }, this.config.pingInterval);
  }

  /**
   * Stop periodic connectivity ping
   */
  private stopPing(): void {
    if (this.pingIntervalId) {
      clearInterval(this.pingIntervalId);
      this.pingIntervalId = null;
    }
  }

  /**
   * Perform a connectivity check via ping
   */
  async checkConnectivity(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.pingTimeout);

      const response = await fetch(this.config.pingUrl, {
        method: 'HEAD',
        cache: 'no-store',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (response.ok) {
        this.updateStatus('online');
        return true;
      }

      this.updateStatus('offline');
      return false;
    } catch {
      this.updateStatus('offline');
      return false;
    }
  }

  /**
   * Get the current network status
   */
  getStatus(): NetworkStatus {
    return this.status;
  }

  /**
   * Check if currently online
   */
  isOnline(): boolean {
    return this.status === 'online';
  }

  /**
   * Subscribe to network status changes
   */
  subscribe(listener: StatusChangeListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /**
   * Queue an action to be executed when network is restored
   */
  queueAction(action: () => Promise<unknown>, description: string): string {
    const id = `queued-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    this.queuedActions.push({
      id,
      action,
      description,
      timestamp: Date.now(),
    });

    errorLoggingService.logInfo({
      message: `Action queued for when network is restored: ${description}`,
      action: 'network-queue-action',
    });

    return id;
  }

  /**
   * Remove a queued action
   */
  removeQueuedAction(id: string): boolean {
    const initialLength = this.queuedActions.length;
    this.queuedActions = this.queuedActions.filter((a) => a.id !== id);
    return this.queuedActions.length < initialLength;
  }

  /**
   * Get all queued actions
   */
  getQueuedActions(): QueuedAction[] {
    return [...this.queuedActions];
  }

  /**
   * Process all queued actions (called when coming back online)
   */
  private async processQueue(): Promise<void> {
    if (this.queuedActions.length === 0) return;

    const actions = [...this.queuedActions];
    this.queuedActions = [];

    errorLoggingService.logInfo({
      message: `Processing ${actions.length} queued action(s)`,
      action: 'network-queue-process',
    });

    for (const queuedAction of actions) {
      try {
        await queuedAction.action();
      } catch (error) {
        errorLoggingService.logError({
          message: `Failed to execute queued action: ${queuedAction.description}`,
          action: 'network-queue-action-failed',
          stack: error instanceof Error ? error.stack : undefined,
        });
      }
    }
  }

  /**
   * Clean up event listeners and intervals
   */
  dispose(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);
    }
    this.stopPing();
    this.listeners = [];
    this.queuedActions = [];
    this.isInitialized = false;
  }
}

// Singleton instance
export const networkStatusService = new NetworkStatusService();
